import { createContext, useContext, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { SERVICE_IDS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QuoteModalContext = createContext(null);

export const useQuoteModal = () => useContext(QuoteModalContext);

export function QuoteModalProvider({ children }) {
  const { t } = useLang();
  const [openState, setOpenState] = useState(false);
  const open = () => setOpenState(true);
  const close = () => setOpenState(false);

  return (
    <QuoteModalContext.Provider value={{ open, close }}>
      {children}
      <QuoteModal open={openState} onClose={close} t={t} />
    </QuoteModalContext.Provider>
  );
}

function QuoteModal({ open, onClose, t }) {
  const [form, setForm] = useState({ name: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiry`, { ...form, email: null, source: "quote-modal" });
      toast.success(t.modal.success, { description: t.modal.successDesc });
      setForm({ name: "", phone: "", service: "", message: "" });
      onClose();
    } catch (err) {
      toast.error(t.modal.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-electric focus:border-electric transition-colors duration-200";

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-md" data-testid="quote-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-navy text-2xl font-bold">{t.modal.title}</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">{t.modal.desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.contactPage.form.name}</label>
            <input required minLength={2} value={form.name} onChange={set("name")} placeholder={t.contactPage.form.namePh} className={inputCls} data-testid="quote-modal-name" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.contactPage.form.phone}</label>
            <input required type="tel" value={form.phone} onChange={set("phone")} placeholder={t.contactPage.form.phonePh} className={inputCls} data-testid="quote-modal-phone" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.contactPage.form.service}</label>
            <select required value={form.service} onChange={set("service")} className={inputCls} data-testid="quote-modal-service">
              <option value="" disabled>{t.contactPage.form.servicePh}</option>
              {SERVICE_IDS.map((id) => (
                <option key={id} value={t.servicesPage.items.find((s) => s.id === id).title}>
                  {t.servicesPage.items.find((s) => s.id === id).title}
                </option>
              ))}
              <option value={t.contactPage.form.other}>{t.contactPage.form.other}</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.contactPage.form.message}</label>
            <textarea required minLength={5} rows={3} value={form.message} onChange={set("message")} placeholder={t.contactPage.form.messagePh} className={inputCls} data-testid="quote-modal-message" />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="quote-modal-submit"
            className="w-full rounded-full bg-electric px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 active:scale-[0.98] transition-all duration-200 disabled:opacity-60"
          >
            {sending ? t.modal.sending : t.modal.submit}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
